import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { MoreVertRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";

export interface DocumentAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  /** Destructive (red) and separated from the others. */
  danger?: boolean;
  disabled?: boolean;
}

/** The "Actions" menu of the document header; hidden when it is empty. */
const DocumentActionsMenu: React.FC<{ actions: DocumentAction[] }> = ({
  actions,
}) => {
  const { t } = useTranslation("documentCrafting");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!actions.length) return null;

  const regular = actions.filter((action) => !action.danger);
  const danger = actions.filter((action) => action.danger);
  const item = (action: DocumentAction) => (
    <MenuItem
      key={action.key}
      disabled={action.disabled}
      onClick={() => {
        setAnchor(null);
        action.onClick();
      }}
      sx={action.danger ? { color: color.red600 } : undefined}
    >
      <ListItemIcon sx={{ color: action.danger ? color.red600 : color.gray500 }}>
        {action.icon}
      </ListItemIcon>
      <ListItemText primaryTypographyProps={{ fontSize: 14 }}>
        {action.label}
      </ListItemText>
    </MenuItem>
  );

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        endIcon={<MoreVertRounded />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
      >
        {t("detail.header.actions")}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {regular.map(item)}
        {regular.length > 0 && danger.length > 0 && <Divider />}
        {danger.map(item)}
      </Menu>
    </>
  );
};

export default DocumentActionsMenu;
